/* ==========================================================================
   DR. DECK — STABILISATION DU MENU DEROULANT (souris uniquement)

   PROBLEME
   Le mega-menu Horizon s'ouvre sur `pointerenter` et se ferme sur
   `pointerleave`, sans aucune temporisation. Traverser la barre de gauche a
   droite ouvrait puis refermait chaque panneau au passage, et l'en-tete
   s'agrandit a chaque fois (--full-open-header-height) : d'ou la sensation
   d'un menu qui saute tout seul.

   Horizon compense la descente vers le panneau par une « safety box »
   (--box-height + data-safety-box, voir header-menu.js) qui n'est active QUE
   tant que le pointeur bouge : des qu'on ralentit en chemin elle disparait et
   le menu se ferme. C'est ce qui donne l'impression qu'il faut viser juste.

   CORRECTIF : deux temporisations d'intention.
   - 120 ms avant d'ouvrir  : un survol de passage n'ouvre plus rien ;
   - 280 ms avant de fermer : on a le temps de descendre en diagonale, et
     revenir sur le panneau annule la fermeture.
   Passer d'un panneau ouvert a un autre reste instantane : l'intention est
   deja etablie, attendre serait penible.

   PORTEE
   - Tactile : non concerne. `layout/theme.liquid` force deja
     `data-menu-style="drawer"` sur tout appareil tactile
     (`'ontouchstart' in window && navigator.maxTouchPoints > 0`), donc
     telephones et tablettes utilisent le tiroir au clic, sans survol.
   - Clavier : non ralenti. Les FocusEvent sont transmis tels quels a Horizon,
     qui a sa propre logique de blur entre elements du sous-menu.
   - Entree « Plus » (overflow) : transmise telle quelle, sa gestion interne
     differe (slot).

   METHODE
   `activate` est un champ d'instance et `deactivate` une methode de
   prototype ; les liaisons declaratives du template (`on:pointerenter=
   "/activate"`) resolvent la methode AU MOMENT de l'evenement. Redefinir les
   deux sur l'instance suffit donc — aucun fichier Liquid du menu n'est
   touche.

   ⚠ GARDE ANTI-DOUBLE-PATCH : ne PAS utiliser `dataset` comme marqueur.
   Horizon re-rend l'en-tete (Section Rendering API, editeur de theme) en
   fusionnant le DOM : l'element survit mais ses attributs sont reecrits, donc
   le marqueur disparait pendant que le wrapper JS, lui, reste en place. Au
   re-passage on emballait le wrapper dans un second wrapper et les delais
   s'additionnaient (240 ms a l'ouverture, 560 ms a la fermeture, bascule
   entre menus redevenue lente). Constate au test. Le marqueur doit vivre en
   JS : WeakSet indexe sur l'instance.
   ========================================================================== */

(() => {
  const DELAI_OUVERTURE = 120;
  const DELAI_FERMETURE = 280;

  const dejaStabilises = new WeakSet();

  const stabiliser = (menu) => {
    if (!menu || dejaStabilises.has(menu)) return;
    if (typeof menu.activate !== 'function' || typeof menu.deactivate !== 'function') return;
    dejaStabilises.add(menu);

    const ouvrirBrut = menu.activate;
    const fermerBrut = menu.deactivate.bind(menu);

    let tOuverture;
    let tFermeture;
    let itemOuvert = null;

    const itemDe = (evenement) =>
      evenement && evenement.target instanceof Element
        ? evenement.target.closest('.menu-list__list-item')
        : null;

    const fermerMaintenant = (evenement) => {
      clearTimeout(tOuverture);
      clearTimeout(tFermeture);
      if (!itemOuvert) return;
      const cible = itemOuvert;
      itemOuvert = null;
      fermerBrut(evenement || { target: cible, relatedTarget: null });
    };

    menu.activate = (evenement) => {
      const cible = itemDe(evenement);

      // Clavier et entree « Plus » : comportement Horizon d'origine.
      if (evenement instanceof FocusEvent || !cible || cible.hasAttribute('slot')) {
        clearTimeout(tFermeture);
        itemOuvert = cible;
        ouvrirBrut(evenement);
        return;
      }

      clearTimeout(tFermeture);
      if (cible === itemOuvert) return;
      clearTimeout(tOuverture);

      const ouvrir = () => {
        itemOuvert = cible;
        ouvrirBrut({ target: cible });
      };

      // Un panneau est deja ouvert : on bascule sans attendre.
      if (itemOuvert) ouvrir();
      else tOuverture = setTimeout(ouvrir, DELAI_OUVERTURE);
    };

    menu.deactivate = (evenement) => {
      if (evenement instanceof FocusEvent) {
        clearTimeout(tOuverture);
        clearTimeout(tFermeture);
        itemOuvert = null;
        fermerBrut(evenement);
        return;
      }

      clearTimeout(tOuverture);
      clearTimeout(tFermeture);
      tFermeture = setTimeout(() => fermerMaintenant(evenement), DELAI_FERMETURE);
    };

    // Quitter franchement l'en-tete ferme sans attendre : plus de panneau
    // orphelin qui reste ouvert quand la souris part vers le catalogue.
    const entete = menu.closest('header-component') || menu;
    entete.addEventListener('pointerleave', () => fermerMaintenant());

    document.addEventListener('keydown', (evenement) => {
      if (evenement.key === 'Escape') fermerMaintenant();
    });
  };

  const appliquer = () => {
    document.querySelectorAll('header-menu').forEach(stabiliser);
  };

  customElements.whenDefined('header-menu').then(() => requestAnimationFrame(appliquer));
  document.addEventListener('DOMContentLoaded', appliquer);

  // L'en-tete est re-rendu par l'editeur de theme et par la Section Rendering
  // API : on re-applique le correctif sur toute nouvelle instance. Le WeakSet
  // garantit qu'une instance deja traitee n'est jamais emballee deux fois.
  const groupe = document.getElementById('header-group') || document.body;
  new MutationObserver(appliquer).observe(groupe, { childList: true, subtree: true });
})();
